/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import { Row, Col } from "react-awesome-styled-grid";
import { useLocation, Link } from "react-router-dom";
import { Box, Heading, Breadcrumbs } from "@nordcloud/gnui";
import { Badge } from "../Badge/Badge";

const normalize = (val: string | undefined) =>
  val ? val.charAt(0).toUpperCase() + val.slice(1) : "";

type TagsProps = {
  color: string;
  text: string;
};

type ComponentLabel = {
  value: string;
  label: string;
};

type ComponentLabelProps = {
  label: string;
  uri: string;
};

type BreadcrumbsBoxProps = {
  title?: string;
  tags?: Array<TagsProps>;
  children?: React.ReactNode;
  labels?: Array<ComponentLabel>;
  customBreadcrumbs?: Array<ComponentLabelProps>;
  generalMapping?: boolean;
};

function BreadcrumbsBox({
  title,
  children,
  tags,
  labels,
  customBreadcrumbs,
  generalMapping,
}: BreadcrumbsBoxProps) {
  const location = useLocation();
  const path: Array<string> = location.pathname.split("/");

  const uniquePath: Array<string> = [
    ...new Set(path.map((item: string) => item)),
  ];

  const displayTitle: string | undefined =
    title || uniquePath.slice(-1).toString();

  const START_PAGE_LABEL = "Home";
  const breadcrumbs = uniquePath.map((item: string, index: number) => {
    const label =
      item === ""
        ? START_PAGE_LABEL
        : labels?.find((el) => el.value === item)?.label || item;
    const uri = `${uniquePath.slice(0, index).join("/")}/${item}`;

    return { label, uri };
  });

  const inlineFlexStyles = { display: "inline-flex", placeItems: "center" };
  const flexStyles = { display: "flex", placeItems: "center" };

  const pageTitle = labels
    ? labels[0].label
    : customBreadcrumbs?.slice(-1)[0].label;

  React.useEffect(() => {
    document.title = `Overlay | ${pageTitle}`;
  }, [pageTitle]);

  return (
    <Box spacing="spacing04">
      <Row style={flexStyles}>
        <Col>
          <div style={inlineFlexStyles}>
            {customBreadcrumbs ? (
              <Heading css={{ minHeight: "2.25rem" }} level={2}>
                {title}
              </Heading>
            ) : (
              <Heading level={2}>
                {!title ? normalize(displayTitle) : displayTitle || ""}
              </Heading>
            )}
            {!generalMapping &&
              tags?.map((tag: TagsProps) => (
                <Badge
                  key={tag.text}
                  css={{ margin: "0 0 0 1rem" }}
                  color={tag.color}
                  text={tag.text}
                />
              ))}
          </div>
          {customBreadcrumbs ? (
            <Breadcrumbs list={customBreadcrumbs} Component={Link} />
          ) : (
            <Breadcrumbs list={breadcrumbs} Component={Link} />
          )}
        </Col>
        {children && (
          <Col style={{ textAlign: "right" }}>
            <div style={{ display: "flex", marginLeft: "auto" }}>
              {children}
            </div>
          </Col>
        )}
      </Row>
    </Box>
  );
}

export default BreadcrumbsBox;
